import { ChildProps } from "./../../../types/types";
export const slugify = (str: string) => {
  return str
    ?.toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const getNestedFieldValue = (obj: any, fieldPath: string) => {
  const modifiedFieldPath = fieldPath?.replace("chatCompany", "company")?.replace("chat.", "cxItem.");
  const fieldParts = modifiedFieldPath?.split(".");
  return fieldParts?.reduce((o, part) => (o ? o[part] : null), obj);
};

const checkCondition = (request: any, condition: Record<string, any>) => {
  if (!condition || Object.keys(condition).length === 0) return true;
  for (const [fieldPath, expectedValue] of Object.entries(condition)) {
    const modifiedFieldPath = fieldPath.replace("chatCompany", "company");
    const fieldValue = getNestedFieldValue(request, modifiedFieldPath);
    const isNotOperator = typeof expectedValue === "string" && expectedValue.startsWith("!");
    const comparisonValue = isNotOperator ? expectedValue.slice(1) : expectedValue;
    if (isNotOperator) {
      return fieldValue !== comparisonValue;
    } else {
      if (fieldValue !== comparisonValue) {
        return false;
      }
    }
  }
  return true;
};

const extractNumber = (value: any): number | null => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const numericString = value.replace(/[^0-9.-]+/g, "");
    const parsed = parseFloat(numericString);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

export type RankData = {
  index: number;
  total: number;
  objectName: string;
  childName: string;
};

/* NEW: GroupBy helper function */
const calculateGroupByResults = (
  items: any[],
  groupByField: string,
  op: string,
  fieldPath: string,
  defaultValue: any
): Record<string, any> => {
  const groupedResult: Record<string, any> = {};
  for (const item of items) {
    let groupValue = getNestedFieldValue(item, groupByField);
    if (groupValue === null || groupValue === undefined) continue;
    groupValue = slugify(String(groupValue));
    if (!groupedResult[groupValue]) {
      groupedResult[groupValue] = {
        amount: 0,
        count: 0,
        min: null as number | null,
        max: null as number | null,
        values: [] as any[],
        accounts: new Set<string>(),
      };
    }
    const fieldValue = getNestedFieldValue(item, fieldPath);
    const value = fieldValue != null ? extractNumber(fieldValue) : defaultValue || 0;
    switch (op) {
    case "sum":
      if (typeof value === "number") {
        groupedResult[groupValue].amount += value;
      }
      break;
    case "count":
      groupedResult[groupValue].count += 1;
      break;
    case "avg":
      if (typeof value === "number") {
        groupedResult[groupValue].amount += value;
        groupedResult[groupValue].count += 1;
      }
      break;
    case "min":
      if (typeof value === "number") {
        if (groupedResult[groupValue].min === null || value < groupedResult[groupValue].min!) {
          groupedResult[groupValue].min = value;
        }
      }
      break;
    case "max":
      if (typeof value === "number") {
        if (groupedResult[groupValue].max === null || value > groupedResult[groupValue].max!) {
          groupedResult[groupValue].max = value;
        }
      }
      break;
    case "concat":
      if (value !== null && value !== undefined) {
        groupedResult[groupValue].values.push(value);
      }
      break;
    default:
      break;
    }
    const orgId = getNestedFieldValue(item, "orgId");
    const companyId = getNestedFieldValue(item, "company._id");
    const accountId = orgId || companyId;
    if (accountId && (accountId?.toLowerCase?.() !== "n/a")) {
      groupedResult[groupValue].accounts.add(String(accountId));
    }
  }
  const finalResult: Record<string, any> = {};
  for (const group in groupedResult) {
    const data = groupedResult[group];
    finalResult[group] = {};
    switch (op) {
    case "sum":
      finalResult[group].amount = data.amount;
      break;
    case "count":
      finalResult[group].count = data.count;
      break;
    case "avg":
      finalResult[group].avg = data.count > 0 ? data.amount / data.count : 0;
      break;
    case "min":
      finalResult[group].min = data.min;
      break;
    case "max":
      finalResult[group].max = data.max;
      break;
    case "concat":
      finalResult[group].values = data.values;
      break;
    default:
      break;
    }
    finalResult[group].accounts = data.accounts.size;
  }
  return finalResult;
};

export const calculateImpactResults = (
  validated: any[],
  pending: any[],
  impactAggregationProperties: any[],
  idea: any
) => {
  const impactResults: Record<string, any> = {};

  impactAggregationProperties.forEach((property: any) => {
    const {
      fieldPath,
      propertyName,
      impact,
      fieldDefaultValue,
      operation = "sum",
      groupBy,
    } = property;

    const displayRanks = impact.displayRanks || [];
    let ranksData: RankData[] | null = null;

    if (groupBy) {
      const totalGrouped = calculateGroupByResults(
        [...validated, ...pending],
        groupBy,
        operation,
        fieldPath,
        fieldDefaultValue
      );
      impactResults[propertyName] = {
        total: totalGrouped,
        ranksData: displayRanks
          ?.map((rankPropertyName: string) => {
            const objectName = rankPropertyName;
            let childName = null;
            let rankData = null;
            if (["domains", "components"].includes(objectName)) {
              if (objectName == "components") {
                childName = idea.componentObj?.name;
                rankData = idea.ranks?.components?.[childName.toLowerCase()];
              } else {
                childName = idea.componentObj?.domain;
                rankData = idea.ranks?.domains?.[childName.toLowerCase()];
              }
            } else if (idea.ranks?.[objectName]) {
              rankData = idea.ranks?.[objectName];
            } else {
              return undefined;
            }
            return {
              index: rankData?.index,
              total: rankData?.total,
              objectName,
              childName,
            };
          })
          .filter((rank: any) => rank !== undefined),
      };
      return;
    }

    let total: number | string = 0;
    let validatedTotal: number | string = 0;
    const pendingCompanyIds: Set<string> = new Set();
    const validatedCompanyIds: Set<string> = new Set();

    const consolidateByCompany = impact?.consolidateByCompany || false;
    const multiplyByComponentWeight = impact?.multiplyByComponentWeight || false;

    const pendingCompanyValues: Record<string, number[]> = {};
    const validatedCompanyValues: Record<string, number[]> = {};

    const processedChatIds = new Set<string>();

    const applyOperation = (values: number[]) => {
      if (values.length === 0) return 0;
      switch (operation) {
      case "concat":
        return values.join(", ");
      case "sum":
        return values.reduce((acc, val) => acc + val, 0);
      case "count":
        return values.length;
      case "avg":
        return values.reduce((acc, val) => acc + val, 0) / values.length;
      case "min":
        return Math.min(...values);
      case "max":
        return Math.max(...values);
      default:
        return 0;
      }
    };

    const shouldConsolidateByChat = fieldPath.includes("cxItem");

    const processRequest = (
      request: any,
      companyValues: Record<string, number[]>,
      companyIds: Set<string>,
      isValidated: boolean,
      defaultValue: number
    ) => {
      const fieldValue = getNestedFieldValue(request, fieldPath);
      const value = fieldValue != null ? extractNumber(fieldValue) : defaultValue || 0;
      const companyId = getNestedFieldValue(request, "company._id");
      const cxItemId = getNestedFieldValue(request, "cxItem._id");

      if (value && !isNaN(value)) {
        if (shouldConsolidateByChat && cxItemId) {
          if (processedChatIds.has(cxItemId)) {
            return;
          }
          processedChatIds.add(cxItemId);
        }

        if (consolidateByCompany && companyId) {
          if (!companyValues[companyId]) {
            companyValues[companyId] = [];
          }
          companyValues[companyId].push(value);
        } else {
          if (isValidated) {
            //@ts-ignore
            validatedTotal += value;
          } else {
            //@ts-ignore
            total += value;
          }
        }
        if (companyId && companyId !== "N/A") companyIds.add(companyId);
      }
    };

    validated.forEach((request) =>
      processRequest(
        request,
        validatedCompanyValues,
        validatedCompanyIds,
        true,
        fieldDefaultValue
      )
    );

    pending.forEach((request) =>
      processRequest(
        request,
        pendingCompanyValues,
        pendingCompanyIds,
        false,
        fieldDefaultValue
      )
    );

    if (consolidateByCompany) {
      validatedTotal = applyOperation(Object.values(validatedCompanyValues).flat());
      total = applyOperation(Object.values(pendingCompanyValues).flat());
    }

    const componentWeight = idea.componentObj?.weight || 1;
    if (multiplyByComponentWeight) {
      if (typeof total === "number") {
        total *= componentWeight;
      }
      if (typeof validatedTotal === "number") {
        validatedTotal *= componentWeight;
      }
    }

    ranksData = displayRanks?.map((rankPropertyName: string) => {
      const objectName = rankPropertyName;
      let childName = null;
      let rankData = null;
      if (["domains", "components"].includes(objectName)) {
        if (objectName == "components") {
          childName = idea.componentObj?.name;
          rankData = idea.ranks?.components?.[childName.toLowerCase()];
        } else {
          childName = idea.componentObj?.domain;
          rankData = idea.ranks?.domains?.[childName.toLowerCase()];
        }
      } else if (idea.ranks?.[objectName]) {
        rankData = idea.ranks?.[objectName];
      } else {
        return undefined;
      }
      return {
        index: rankData?.index,
        total: rankData?.total,
        objectName,
        childName,
      };
    });

    impactResults[propertyName] = {
      total,
      validatedTotal,
      customerCount: pendingCompanyIds.size,
      validatedCustomerCount: validatedCompanyIds.size,
      ranksData: ranksData?.filter((rank) => rank !== undefined),
    };
  });

  return impactResults;
};